import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { CalendarCheck, HeartHandshake, Star, Clock, ArrowRight, TrendingUp } from "lucide-react";

export default function Overview({ goTo }) {
  const { currentUser, userProfile } = useAuth();
  const [stats, setStats] = useState({ attendance: 0, totalEvents: 0, donations: 0, donationCount: 0 });
  const [recentDonations, setRecentDonations] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const name = userProfile?.name || currentUser?.email?.split("@")[0] || "Member";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useEffect(() => {
  async function loadData() {
    try {
      // Attendance stats
      const attQ   = query(collection(db,"attendance"), where("userId","==",currentUser.uid));
      const attSnap = await getDocs(attQ);
      const attDocs = attSnap.docs.map(d => d.data());
      const present = attDocs.filter(d => d.status === "present").length;

      // Donations
      const donQ   = query(collection(db,"donations"), where("userId","==",currentUser.uid));
      const donSnap = await getDocs(donQ);
      const donDocs = donSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      const totalMoney = donDocs
        .filter(d => d.type === "money")
        .reduce((a,b) => a + (b.amount||0), 0);

      // ── Events — fetch ALL upcoming events ──
      const today = new Date().toISOString().split("T")[0];
      const evtSnap = await getDocs(collection(db, "events"));
      const allEvents = evtSnap.docs
        .map(d => ({ id:d.id, ...d.data() }))
        .filter(e => e.date >= today)        // only upcoming
        .sort((a,b) => a.date.localeCompare(b.date)) // sort by date
        .slice(0, 4);                         // show max 4

      setStats({
        attendance:    present,
        totalEvents:   attDocs.length,
        donations:     totalMoney,
        donationCount: donDocs.length
      });
      setRecentDonations(donDocs.sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0,3));
      setUpcomingEvents(allEvents);

    } catch(err) { console.error(err); }
    finally { setLoading(false); }
  }
  if (currentUser) loadData();
}, [currentUser]);

  const pct = stats.totalEvents > 0 ? Math.round((stats.attendance / stats.totalEvents) * 100) : 0;

  const METRIC_CARDS = [
    { label: "Attendance Rate", value: `${pct}%`, sub: `${stats.attendance} of ${stats.totalEvents} events`, icon: CalendarCheck, color: "#22c55e", onClick: () => goTo("attendance") },
    { label: "Total Donated", value: `₹${stats.donations.toLocaleString()}`, sub: `${stats.donationCount} transactions`, icon: HeartHandshake, color: "#f59e0b", onClick: () => goTo("donations") },
    { label: "Member Since", value: userProfile?.joinDate?.split("-")[0] || "2024", sub: userProfile?.joinDate || "Active member", icon: Star, color: "#a855f7", onClick: () => goTo("profile") },
    { label: "Member ID", value: userProfile?.memberId || "HTS-0000", sub: userProfile?.role?.toUpperCase() || "MEMBER", icon: Clock, color: "#3b82f6", onClick: () => goTo("profile") },
  ];

  return (
    <div>
      {/* Greeting */}
      <div style={s.greeting}>
        <div>
          <h1 style={s.greetTitle}>{greeting}, {name.split(" ")[0]} 👋</h1>
          <p style={s.greetSub}>Here's your activity summary — {new Date().toLocaleDateString("en-IN", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}</p>
        </div>
      </div>

      {/* Metric Cards */}
      <div style={s.metricsGrid}>
        {METRIC_CARDS.map(({label,value,sub,icon:Icon,color,onClick}) => (
          <div key={label} onClick={onClick} style={{...s.metricCard, cursor:"pointer"}} className="metric-hover">
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
              <div style={{...s.metricIcon, background:`${color}18`, border:`1px solid ${color}28`}}>
                <Icon size={20} color={color}/>
              </div>
              <ArrowRight size={14} color="#52504d"/>
            </div>
            <div style={{...s.metricVal, color}}>{value}</div>
            <div style={s.metricLabel}>{label}</div>
            <div style={s.metricSub}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={s.grid2}>
        {/* Upcoming Events */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            Upcoming Events
            <span style={s.cardBadge}>{upcomingEvents.length} scheduled</span>
          </div>
          {loading ? (
            <div style={s.empty}>Loading events...</div>
          ) : upcomingEvents.length === 0 ? (
            <div style={s.empty}>No upcoming events</div>
          ) : upcomingEvents.map((evt, i) => {
            const colors = ["#22c55e","#3b82f6","#a855f7","#f97316"];
            return (
              <div key={evt.id} style={s.evtRow}>
                <div style={{...s.evtDot, background: colors[i % colors.length]}}/>
                <div style={{flex:1}}>
                  <div style={s.evtName}>{evt.title}</div>
                  <div style={s.evtLoc}>{evt.location}</div>
                </div>
                <div style={s.evtDate}>{evt.date}</div>
              </div>
            );
          })}
          {upcomingEvents.length === 0 && (
            <>
              {[["Food Drive – Loni","Loni, Ghaziabad","May 10","#22c55e"],["Shiksha Camp","HTS Centre","May 14","#3b82f6"],["Hygiene Drive","Sahibabad","May 18","#a855f7"]].map(([t,l,d,c])=>(
                <div key={t} style={s.evtRow}>
                  <div style={{...s.evtDot,background:c}}/>
                  <div style={{flex:1}}><div style={s.evtName}>{t}</div><div style={s.evtLoc}>{l}</div></div>
                  <div style={s.evtDate}>{d}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Attendance Bar Chart */}
        <div style={s.card}>
          <div style={s.cardTitle}>Monthly Attendance <TrendingUp size={14} color="#22c55e"/></div>
          {[["Jan","90"],["Feb","75"],["Mar","85"],["Apr","85"],["May","70"]].map(([m,p])=>(
            <div key={m} style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{fontSize:11,color:"#78716c",width:28,flexShrink:0}}>{m}</div>
              <div style={{flex:1,height:8,background:"rgba(255,255,255,0.06)",borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${p}%`,borderRadius:4,background:parseInt(p)>=80?"#22c55e":parseInt(p)>=60?"#f59e0b":"#ef4444",transition:"width 1s ease"}}/>
              </div>
              <div style={{fontSize:11,color:"#78716c",width:34,textAlign:"right"}}>{p}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Donations */}
      <div style={s.card}>
        <div style={{...s.cardTitle, marginBottom:16}}>
          Recent Donations
          <button onClick={()=>goTo("donations")} style={s.viewAllBtn}>View All <ArrowRight size={12}/></button>
        </div>
        {recentDonations.length === 0 ? (
          <div style={s.empty}>No donations recorded yet</div>
        ) : recentDonations.map(don => (
          <div key={don.id} style={s.donRow}>
            <div style={{...s.donIcon, background: don.type==="money" ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)"}}>
              <HeartHandshake size={16} color={don.type==="money"?"#22c55e":"#f59e0b"}/>
            </div>
            <div style={{flex:1}}>
              <div style={s.donName}>{don.type==="money" ? "Cash Donation" : `${don.items?.join(", ") || "Item Donation"}`}</div>
              <div style={s.donDate}>{don.date}</div>
            </div>
            <div style={s.donAmt}>{don.type==="money" ? `₹${don.amount?.toLocaleString()}` : "Items"}</div>
          </div>
        ))}
      </div>
      <style>{`.metric-hover:hover{transform:translateY(-3px);border-color:rgba(255,255,255,0.12)!important;} .metric-hover{transition:all 0.2s;}`}</style>
    </div>
  );
}

const s = {
  greeting: { marginBottom:24 },
  greetTitle: { fontFamily:"'Playfair Display',serif", fontSize:"clamp(22px,3vw,32px)", fontWeight:800, color:"#fef3e2", margin:"0 0 6px" },
  greetSub: { fontSize:13, color:"#78716c", margin:0 },
  metricsGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14, marginBottom:20 },
  metricCard: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  metricIcon: { width:40, height:40, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center" },
  metricVal: { fontSize:26, fontWeight:800, fontFamily:"'Playfair Display',serif", lineHeight:1, margin:"0 0 4px" },
  metricLabel: { fontSize:13, fontWeight:600, color:"#fef3e2", margin:"0 0 3px" },
  metricSub: { fontSize:11, color:"#52504d" },
  grid2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 },
  card: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18, marginBottom:16 },
  cardTitle: { fontSize:14, fontWeight:700, color:"#fef3e2", marginBottom:14, display:"flex", alignItems:"center", gap:8, justifyContent:"space-between" },
  cardBadge: { fontSize:10, background:"rgba(59,130,246,0.15)", color:"#3b82f6", border:"1px solid rgba(59,130,246,0.25)", padding:"2px 8px", borderRadius:50, fontWeight:700 },
  evtRow: { display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" },
  evtDot: { width:8, height:8, borderRadius:"50%", flexShrink:0 },
  evtName: { fontSize:13, fontWeight:600, color:"#fef3e2" },
  evtLoc: { fontSize:11, color:"#52504d", marginTop:2 },
  evtDate: { fontSize:11, color:"#78716c", flexShrink:0 },
  donRow: { display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" },
  donIcon: { width:36, height:36, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  donName: { fontSize:13, fontWeight:600, color:"#fef3e2" },
  donDate: { fontSize:11, color:"#52504d", marginTop:2 },
  donAmt: { fontSize:14, fontWeight:700, color:"#fef3e2", flexShrink:0 },
  viewAllBtn: { display:"inline-flex", alignItems:"center", gap:4, background:"none", border:"none", color:"#f59e0b", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  empty: { fontSize:13, color:"#52504d", textAlign:"center", padding:"20px 0" },
};
